/**
 * Staff Portal — student profile routing helpers.
 *
 * Class Schedules surfaces (both the standalone /class-schedules page and
 * the schedules embedded in Staff Detail) are reachable by admins AND by
 * staff-role users. When a student chip / view-student action is clicked
 * we must send the two audiences to different profile pages:
 *
 *   • Admin  → /student-history/:id             (StudentHistory, admin scope)
 *   • Staff  → /staff-portal/students/:studentId (StudentView, staff scope)
 *
 * Both destinations exist; both accept the same numeric students.id. The
 * bug this replaces: everyone was sent to /student-history/:id, which for
 * staff-role users lands on the admin StudentHistory page that has no
 * staff-scoped data → renders "No data available".
 *
 * We centralise the path construction here so any second Staff-Portal call
 * site (there are already five) uses the same rule and stays in sync.
 *
 * ─────────────────────────────────────────────────────────────────────
 * Create-Class routing (Sep 2026 — second reason to hoist this file):
 *
 * The admin Create-Class form (/assign/classes/create → studentcreate.tsx)
 * opens the StaffPicker so an admin can create a class for any teacher.
 * A staff-role user landing on that page has no legitimate use for the
 * StaffPicker — the backend derives `staff_id` from Auth for non-admin
 * callers and rejects anything else. The Staff Portal ships a dedicated
 * /staff-portal/classes/create page (CreateClass.tsx) that locks the
 * staff identity and hydrates Subjects/Grades from the caller's own
 * teaching assignments.
 *
 * Every Staff-Portal-reachable "Create Class" / "Add Class" button that
 * used to hardcode the admin path is now routed through
 * `useCreateClassPath()` so staff-role users land on the staff page and
 * admins keep their existing form. This is the same "list scope must
 * match detail authz" rule as the student-profile fix, applied to the
 * class-create surface.
 */

import { useMemo } from "react";
import useUser from "../../../Core/hook/useUser";

/** Admin student profile route (unchanged canonical). */
export const adminStudentProfilePath = (id: number | string): string =>
  `/student-history/${encodeURIComponent(String(id))}`;

/** Staff Portal student profile route (StaffPortalController::studentDetail-backed). */
export const staffStudentProfilePath = (id: number | string): string =>
  `/staff-portal/students/${encodeURIComponent(String(id))}`;

/**
 * Hook returning a builder that picks the correct student profile path for
 * the currently-authenticated user. Staff-role users get the staff-portal
 * destination; everyone else (System_Admin, other admin roles) gets the
 * canonical admin route so no admin behaviour changes.
 *
 * Parent users never reach these call sites (Class Schedules is not in the
 * Parent Portal), so we don't branch on isParentUser here.
 */
export function useStudentProfilePath(): (id: number | string) => string {
  const { isStaffRole } = useUser();
  return useMemo(
    () => (id: number | string) =>
      isStaffRole ? staffStudentProfilePath(id) : adminStudentProfilePath(id),
    [isStaffRole]
  );
}

// ── Create Class routing ──────────────────────────────────────────────

/**
 * Staff Portal Create-Class route. Locked to the acting staff (backend
 * overwrites `staff_id` from Auth). Subjects/Grades come from the
 * caller's `staff_subject_grade_assignment` rows. No StaffPicker is
 * rendered.
 */
export const staffCreateClassPath = (): string => "/staff-portal/classes/create";

/**
 * Admin Create-Class route (unchanged canonical). Renders the shared
 * studentcreate.tsx surface with the StaffPicker so an admin can create
 * a class for any teacher and optionally pre-select a student.
 *
 * Query-string tack-on (return_url / source / prefilled staff/student id)
 * is the caller's responsibility — this helper only returns the base path
 * so both branches remain composable with `buildCreateReturnSearch()`.
 */
export const adminCreateClassPath = (): string => "/assign/classes/create";

/**
 * Hook returning a builder for the correct Create-Class path for the
 * currently-authenticated user. Staff-role → staff page; everyone else
 * (System_Admin, other admin roles) → admin page. Admin behaviour is
 * unchanged.
 *
 * Callers that need to preserve a return URL / assignment source can
 * safely append their own query string to the returned value — the staff
 * page ignores unknown params, the admin page reads them the same as
 * before, so the branching stays transparent to the round-trip.
 */
export function useCreateClassPath(): () => string {
  const { isStaffRole } = useUser();
  return useMemo(
    () => () => (isStaffRole ? staffCreateClassPath() : adminCreateClassPath()),
    [isStaffRole]
  );
}
